import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, AlertCircle } from "lucide-react";
import { jobTitlesApi, type CreateJobTitleDto } from "@/lib/api/job-titles";
import { JOB_TITLES_QUERY_KEY } from "@/hooks/useJobTitles";
import { cn } from "@/lib/utils";

const JobTitleFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateJobTitleDto>({
    name: "",
  });

  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadJobTitle(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  const [isActive, setIsActive] = useState(true);

  const loadJobTitle = async (jobTitleId: string) => {
    try {
      setLoading(true);
      const jobTitle = await jobTitlesApi.getById(jobTitleId);
      setFormData({
        name: jobTitle.name,
      });
      setIsActive(jobTitle.isActive);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load job title.");
      navigate("/setup/job-titles");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    setErrors(newErrors);
    setTouched(new Set(["name"]));
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
    if (field === "name") {
      const error = !formData.name || !formData.name.trim() ? "Name is required" : "";
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      if (isEditing && id) {
        await jobTitlesApi.update(id, { ...formData, isActive });
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        // For creation, don't send isActive - backend defaults to true
        await jobTitlesApi.create(formData);
        toast.success(`"${formData.name}" has been created successfully.`);
      }
      queryClient.invalidateQueries(JOB_TITLES_QUERY_KEY);
      navigate("/setup/job-titles");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage job titles.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading job title..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="form">
        <PageHeader
          title={isEditing ? "Edit Job Title" : "Create Job Title"}
          description={
            isEditing ? "Update job title information" : "Create a new professional job title"
          }
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Job Titles", href: "/setup/job-titles" },
            { label: isEditing ? "Edit" : "New" },
          ]}
          showSearch={false}
        />

        <form onSubmit={handleSubmit}>
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="e.g., Librarian, Assistant Librarian"
                  className={cn(
                    touched.has("name") && errors.name && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("name") && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.name}
                  </p>
                )}
              </div>

              {isEditing && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <Checkbox
                    id="isActive"
                    checked={isActive}
                    onCheckedChange={(checked) => setIsActive(checked === true)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="isActive" className="font-medium cursor-pointer">
                      Active
                    </Label>
                    <p className="text-xs text-gray-500">
                      Only active job titles can be assigned to staff
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => navigate("/setup/job-titles")}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Job Title"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>
    </AppLayout>
  );
};

export default JobTitleFormPage;
